/** Finance → Payout Responsibility (comparison only — the live Payout Run is unchanged). */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import useAuthStore from '../../lib/auth-store';
import { Card, PageHeader, TenantSelect, btnGhost, btnPrimary, errorText, useTenantScope } from '../../components/finance/financeUi';
import { ReviewQueue } from '../../components/payout-routing/ReviewQueue';
import { ComparisonResult } from '../../components/payout-routing/ComparisonResult';

function Migration({ scope, onApplied }) {
  const [preview, setPreview] = useState(null);
  useEffect(() => setPreview(null), [scope.tenantId]);
  const run = async (apply) => {
    try {
      if (apply) {
        const r = await api.request('/payout-routing/migration/apply', { method: 'POST', body: JSON.stringify(scope.body) });
        toast.success(`${r.created} records created — all stay root-paid, needs review`); onApplied();
      }
      setPreview(await api.request(`/payout-routing/migration/preview?${scope.query}`));
    } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
      <button type="button" className={btnGhost} onClick={() => run(false)} data-testid="migration-preview-btn">Preview migration</button>
      <button type="button" className={btnPrimary} disabled={!preview || !preview.to_create} onClick={() => run(true)} data-testid="migration-apply-btn">Create review records</button>
      {preview && <span data-testid="migration-summary">{preview.candidates} compensated parties · {preview.already_migrated} already reviewed · {preview.to_create} to create. {preview.effect}</span>}
    </div>
  );
}

function Comparison({ scope }) {
  const [periods, setPeriods] = useState([]);
  const [picked, setPicked] = useState([]);
  const [mode, setMode] = useState('effective');
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!scope.tenantId) return;
    api.request(`/payout-routing/periods?${scope.query}`).then((p) => { setPeriods(p); setPicked(p.slice(0, 3).map((x) => x.period)); }).catch(() => setPeriods([]));
  }, [scope.tenantId, scope.query]);
  const toggle = (p) => setPicked(picked.includes(p) ? picked.filter((x) => x !== p) : [...picked, p]);
  const run = async () => {
    try { setResult(await api.request('/payout-routing/comparisons', { method: 'POST', body: JSON.stringify({ tenant_id: scope.tenantId, periods: picked, mode }) })); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="space-y-3 p-4">
      <div className="flex flex-wrap gap-2" data-testid="comparison-periods">
        {!periods.length && <span className="text-sm text-muted-foreground">No calculated periods for this tenant yet.</span>}
        {[...new Set(periods.map((p) => p.period))].map((p) => (
          <button key={p} type="button" onClick={() => toggle(p)} data-testid={`period-chip-${p}`}
                  className={`rounded-full border px-3 py-1 text-xs ${picked.includes(p) ? 'bg-primary text-primary-foreground' : ''}`}>{p}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <select className="h-10 rounded-md border bg-background px-3 text-sm" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="comparison-mode">
          <option value="effective">Records in force for each period</option>
          <option value="what_if">What-if: include proposals and approved changes</option>
        </select>
        <button type="button" className={btnPrimary} disabled={!picked.length} onClick={run} data-testid="run-comparison-btn">Run comparison</button>
        {result && <span className="text-sm" data-testid="comparison-overall-gate">Overall: <b>{result.gate}</b></span>}
      </div>
      {result && result.results.map((c) => <ComparisonResult key={c.comparison_id} c={c} />)}
    </div>
  );
}

export default function PayoutResponsibilityPage() {
  const { user } = useAuthStore();
  const scope = useTenantScope(user);
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState(null);
  const [flags, setFlags] = useState(null);
  useEffect(() => {
    if (!scope.tenantId) return;
    api.request(`/payout-routing/tenant-flags?${scope.query}`).then(setFlags).catch(() => setFlags(null));
  }, [scope.tenantId, scope.query]);
  const load = useCallback(() => {
    if (!scope.tenantId) return;
    api.request(`/payout-routing/review?${scope.query}`).then(setRows).catch((e) => toast.error(errorText(e)));
  }, [scope.tenantId, scope.query]);
  useEffect(() => { api.request('/payout-routing/options').then(setOptions).catch(() => {}); }, []);
  useEffect(load, [load]);
  return (
    <div className="space-y-6" data-testid="payout-responsibility-page">
      <PageHeader title="Payout Responsibility" subtitle="Who pays whom — review and comparison only. Once a tenant's cutover is authoritative, approved payers own their runs in Payer Payout Runs.">
        <TenantSelect scope={scope} testid="payout-routing-tenant" />
      </PageHeader>
      {flags && flags.synthetic && (
        <div className="rounded-xl border-2 border-dashed border-amber-500 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900" data-testid="synthetic-data-banner">
          Synthetic Demo Data — No Real Payments · {flags.financial_data} · excluded from production reporting and real payouts
        </div>
      )}
      <Card title="Migration" testid="migration-card"><Migration scope={scope} onApplied={load} /></Card>
      <Card title={`Review queue (${rows.length})`} testid="review-card">{options && <ReviewQueue rows={rows} options={options} onChange={load} />}</Card>
      <Card title="Routing comparison" testid="comparison-card"><Comparison scope={scope} /></Card>
    </div>
  );
}
